import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, landingForRole } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LockKeyhole, Mail, ShieldCheck, Clock } from "lucide-react";

const QUICK_LOGINS = [
  { label: "Super Admin", email: "superadmin@mama.mg", pwd: "Admin@MAMA2026" },
  { label: "Admin Agence", email: "admin.tnr@mama.mg", pwd: "Agency@2026" },
  { label: "Accueil", email: "accueil.tnr@mama.mg", pwd: "Accueil@2026" },
  { label: "Agent Guichet", email: "agent.tnr@mama.mg", pwd: "Agent@2026" },
  { label: "Écran TV", email: "ecran.tnr@mama.mg", pwd: "Ecran@2026" },
  { label: "Auditeur", email: "audit@mama.mg", pwd: "Audit@2026" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const expired = params.get("expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (expired) {
      toast.info("Votre session a expiré. Veuillez vous reconnecter.");
    }
  }, [expired]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Bienvenue, ${res.user.full_name}`);
      const target = loc.state?.from || landingForRole(res.user.role);
      nav(target, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const quickFill = (q) => {
    setEmail(q.email);
    setPassword(q.pwd);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-white">
      {/* Left visual panel */}
      <div className="hidden lg:flex lg:col-span-2 bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
             style={{ backgroundImage: "radial-gradient(#0F172A 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="MAMA" className="w-16 h-16" />
            <div>
              <div className="font-display font-bold text-2xl text-slate-900">MAMA</div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Mutuelle d'Assurance Malagasy</div>
            </div>
          </div>
          <div className="space-y-6">
            <h1 className="font-display font-black text-5xl text-slate-900 leading-tight">
              Système de gestion <br />
              <span className="text-[#DC2626]">des files d'attente</span>
            </h1>
            <p className="text-slate-600 text-base leading-relaxed max-w-md">
              Plateforme institutionnelle pour l'accueil, le suivi et le service de la clientèle
              dans toutes les agences MAMA — Antananarivo, Toamasina, Antsirabe.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
              Authentification sécurisée — JWT chiffré, cookies httpOnly
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div className="text-xs text-slate-400">© 2026 MAMA • Madagascar</div>
            <div className="flex gap-1.5">
              <div className="h-1 w-12 bg-[#DC2626]" />
              <div className="h-1 w-12 bg-white border border-slate-200" />
              <div className="h-1 w-12 bg-[#16A34A]" />
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:col-span-3 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <img src={LOGO_URL} alt="MAMA" className="w-12 h-12" />
            <div>
              <div className="font-display font-bold text-xl">MAMA</div>
              <div className="text-xs text-slate-500">Mutuelle d'Assurance Malagasy</div>
            </div>
          </div>

          <div>
            <h2 className="font-display font-bold text-3xl text-slate-900">Connexion</h2>
            <p className="text-slate-500 text-sm mt-2">
              Identifiez-vous pour accéder à votre espace de travail.
            </p>
          </div>

          {expired && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 flex items-start gap-3"
                 data-testid="session-expired-banner">
              <Clock className="w-5 h-5 mt-0.5 text-amber-700 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">Session expirée</div>
                <div className="text-amber-800">Pour des raisons de sécurité, veuillez vous reconnecter.</div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 text-sm">Adresse e-mail</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email" type="email" required autoComplete="email"
                  data-testid="login-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="agent@mama.mg"
                  className="pl-9 h-11 bg-white border-slate-300 focus:border-[#DC2626] focus:ring-[#DC2626]/20"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pwd" className="text-slate-700 text-sm">Mot de passe</Label>
              <div className="relative">
                <LockKeyhole className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="pwd" type="password" required autoComplete="current-password"
                  data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 h-11 bg-white border-slate-300 focus:border-[#DC2626] focus:ring-[#DC2626]/20"
                />
              </div>
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold tracking-wide"
            >
              {busy ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <div className="pt-6 border-t border-slate-200 space-y-3">
            <div className="text-xs uppercase tracking-widest text-slate-400">Comptes de démonstration</div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_LOGINS.map((q) => (
                <button
                  key={q.email}
                  type="button"
                  data-testid={`quick-login-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => quickFill(q)}
                  className="text-left px-3 py-2 rounded-md border border-slate-200 hover:border-[#DC2626] hover:bg-red-50/30 transition text-sm"
                >
                  <div className="font-medium text-slate-900">{q.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{q.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
